import { useCallback, useEffect, useRef, useState } from "react";
import { fetchConfig } from "./use-config";

export interface CdcScriptLoadResult {
  success: boolean;
  error?: string;
}

export interface ShowScreenSetOptions {
  screenSet: string;
  startScreen: string;
  onAfterSubmit?: any;
}

type GlobalError = string | null | Error;

export interface UseCdcScriptResult {
  isLoading: boolean;
  error: GlobalError;
  setError: (error: GlobalError) => void;
  loadScript: () => Promise<CdcScriptLoadResult>;
  isShowScreenSetLoading: boolean;
  showScreenSet: (options: ShowScreenSetOptions) => void;
  setGlobalError: (error: GlobalError) => void;
}

// Global state for CDC script loading
let globalLoading = false;
let globalLoadPromise: Promise<CdcScriptLoadResult> | null = null;
let globalError: GlobalError = null;
const listeners: Set<(loading: boolean) => void> = new Set();
const errorListeners: Set<(error: GlobalError) => void> = new Set();

function setGlobalLoading(loading: boolean) {
  globalLoading = loading;
  listeners.forEach((setLoading) => {
    setLoading(loading);
  });
}

function setGlobalError(error: GlobalError) {
  globalError = error;
  errorListeners.forEach((setError) => {
    setError(error);
  });
}

const loadCdcScript = async (): Promise<CdcScriptLoadResult> => {
  if (globalLoading && globalLoadPromise) {
    return globalLoadPromise;
  }

  setGlobalLoading(true);
  globalLoadPromise = performLoad();

  const result = await globalLoadPromise;
  setGlobalLoading(false);

  return result;
};

const performLoad = async (): Promise<CdcScriptLoadResult> => {
  try {
    const config = await fetchConfig();

    if (!config.cdcSdk) {
      const error = "CDC SDK URL not found in config";
      setGlobalError(error);
      return { success: false, error };
    }

    return await injectScript(config.cdcSdk);
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : "Unknown error occurred";
    const errorFinal = `Failed to load CDC config: ${errorMessage}`;
    setGlobalError(errorFinal);
    return { success: false, error: errorFinal };
  }
};

const injectScript = async (
  cdcSdkUrl: string,
  retryCount = 0,
): Promise<CdcScriptLoadResult> => {
  const maxRetries = 3;
  const retryDelay = 300; // 0.3 second
  const timeout = 10000; // 10 seconds timeout

  return new Promise((resolve) => {
    if (document.querySelector(`script[src="${cdcSdkUrl}"]`)) {
      resolve({ success: true });
      return;
    }

    const scriptTag = document.createElement("script");
    scriptTag.src = cdcSdkUrl;
    scriptTag.type = "text/javascript";
    scriptTag.async = true;

    let isResolved = false;

    // Timeout handler
    const timeoutId = setTimeout(() => {
      if (!isResolved) {
        isResolved = true;
        console.warn(`CDC script load timeout after ${timeout}ms`);
        handleFailure("Script load timeout");
      }
    }, timeout);

    const handleSuccess = () => {
      if (!isResolved) {
        isResolved = true;
        clearTimeout(timeoutId);
        resolve({ success: true });
      }
    };

    const handleFailure = async (reason: string) => {
      if (isResolved) return;

      isResolved = true;
      clearTimeout(timeoutId);

      // Remove failed script tag
      scriptTag.remove();

      if (retryCount < maxRetries) {
        console.warn(
          `CDC script load failed (${reason}), retrying... (${retryCount + 1}/${maxRetries})`,
        );

        // Wait before retry
        await new Promise((resolve) => setTimeout(resolve, retryDelay));

        // Retry
        const retryResult = await injectScript(cdcSdkUrl, retryCount + 1);
        resolve(retryResult);
      } else {
        const error = `Failed to load CDC script after ${maxRetries} attempts from: ${cdcSdkUrl}. Last error: ${reason}`;
        setGlobalError(error);
        resolve({ success: false, error });
      }
    };

    scriptTag.onload = handleSuccess;
    scriptTag.onerror = () => handleFailure("Script error");

    document.head.appendChild(scriptTag);
  });
};

export function useCdcScript(autoLoad: boolean = false): UseCdcScriptResult {
  const [isLoading, setIsLoading] = useState(globalLoading);
  const [isShowScreenSetLoading, setIsShowScreenSetLoading] = useState(false);
  const [error, setError] = useState<GlobalError>(globalError);
  const isShowScreenSetLoadingRef = useRef(false);

  useEffect(() => {
    // Sync global state
    setIsLoading(globalLoading);
    setError(globalError);

    listeners.add(setIsLoading);
    errorListeners.add(setError);

    if (autoLoad && !globalLoading) {
      loadScript();
    }

    return () => {
      listeners.delete(setIsLoading);
      errorListeners.delete(setError);
    };
  }, [autoLoad]);

  const loadScript = async (): Promise<CdcScriptLoadResult> => {
    setGlobalError(null);

    try {
      const result = await loadCdcScript();

      if (!result.success) {
        setGlobalError(result.error || "Failed to load CDC script");
      }

      return result;
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Unknown error occurred";
      setGlobalError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const showScreenSet = useCallback((options: ShowScreenSetOptions) => {
    if (!window.gigya?.accounts?.showScreenSet) {
      setGlobalError("CDC script not loaded yet");
      return;
    }

    setIsShowScreenSetLoading(true);
    isShowScreenSetLoadingRef.current = true;

    const enhancedOptions = {
      ...options,
      onAfterScreenLoad: () => {
        setIsShowScreenSetLoading(false);
        isShowScreenSetLoadingRef.current = false;
        setGlobalError(null);
      },
      onError: (error: any) => {
        setIsShowScreenSetLoading(false);
        isShowScreenSetLoadingRef.current = false;
        let errorMsg: string;

        if (typeof error === "string") {
          errorMsg = error;
        } else if (
          error &&
          typeof error === "object" &&
          typeof error.errorMessage === "string"
        ) {
          errorMsg = error.errorMessage;
        } else {
          errorMsg = "An unexpected error occurred. Please try again.";
          console.log(`${errorMsg} - ${JSON.stringify(error)}`);
        }

        setGlobalError(errorMsg);
      },
      onHide: () => {
        setIsShowScreenSetLoading(false);
        isShowScreenSetLoadingRef.current = false;
      },
    };

    try {
      window.gigya.accounts.showScreenSet(enhancedOptions);

      // Fallback timeout in case events don't fire
      setTimeout(() => {
        if (isShowScreenSetLoadingRef.current) {
          setGlobalError("Timeout reached, removing loading state");
          setIsShowScreenSetLoading(false);
          isShowScreenSetLoadingRef.current = false;
        }
      }, 10000);
    } catch (error) {
      setIsShowScreenSetLoading(false);
      isShowScreenSetLoadingRef.current = false;
      setGlobalError(error as GlobalError);
    }
  }, []);

  return {
    isLoading,
    isShowScreenSetLoading,
    showScreenSet,
    error,
    setError,
    loadScript,
    setGlobalError,
  };
}

// Export the load function for direct usage if needed
export const loadCdcScriptDirect = loadCdcScript;
